import {z} from 'zod'
import {ReactNode, useMemo} from 'react'
import {Controller, useFormContext} from 'react-hook-form'
import {GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ObjectKeys, pickPropsFromObj} from '@/utilities/helpers'
import {Switch} from '@/components/commons/switch/Switch'
import {useTranslation} from '@/translations/i18n'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {InputText} from '@/components/commons/input-text/InputText'
import {disabilityBenefitsMapToLabel} from '../../utils'
import {MedicalReportFieldsSchema} from '../../formSchemas'
import {SwitchField} from '../switch-field/SwitchField'

const DisabilityBenefits = MedicalReportFieldsSchema.pick({
    civilDisability: true,
    companionAllowance: true,
    attendanceAllowance: true,
    law104Article3Paragraph1: true,
    law104Article3Paragraph3: true,
    disabilityPercentage: true,
    companionAllowanceReviewable: true,
    attendanceAllowanceReviewable: true
})
type DisabilityBenefits = z.infer<typeof DisabilityBenefits>

export const DisabilityBenefitsFields = () => {
    const {t} = useTranslation()
    const form = useFormContext<DisabilityBenefits>()

    const switches = useMemo(
        () =>
            pickPropsFromObj(disabilityBenefitsMapToLabel, [
                'civilDisability',
                'companionAllowance',
                'attendanceAllowance'
            ]),
        []
    )

    const lawCheckboxes = useMemo(
        () => pickPropsFromObj(disabilityBenefitsMapToLabel, ['law104Article3Paragraph1', 'law104Article3Paragraph3']),
        []
    )

    const relatedFieldKeys = useMemo(
        () =>
            pickPropsFromObj(disabilityBenefitsMapToLabel, [
                'disabilityPercentage',
                'companionAllowanceReviewable',
                'attendanceAllowanceReviewable'
            ]),
        []
    )

    const rendererKeyMap = {
        civilDisability: 'disabilityPercentage',
        companionAllowance: 'companionAllowanceReviewable',
        attendanceAllowance: 'attendanceAllowanceReviewable'
    } as const satisfies Record<keyof typeof switches, keyof typeof relatedFieldKeys>

    const renderSwitchRelatedField = (keyField: keyof typeof switches) => {
        if (!form.getValues(keyField)) return null

        const rendererComponentMap = {
            civilDisability: (
                <InputText
                    type="number"
                    label={t(disabilityBenefitsMapToLabel[rendererKeyMap[keyField]])}
                    placeholder="0"
                    width="fit-content"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    step="1"
                    {...form.register(rendererKeyMap[keyField], {
                        setValueAs: v => {
                            return v == '' ? 0 : parseFloat(v).toFixed(2)
                        }
                    })}
                />
            ),
            companionAllowance: (
                <Checkbox
                    id={rendererKeyMap[keyField]}
                    label={t(disabilityBenefitsMapToLabel[rendererKeyMap[keyField]])}
                    {...form.register(rendererKeyMap[keyField])}
                />
            ),
            attendanceAllowance: (
                <Checkbox
                    id={rendererKeyMap[keyField]}
                    label={t(disabilityBenefitsMapToLabel[rendererKeyMap[keyField]])}
                    {...form.register(rendererKeyMap[keyField])}
                />
            )
        } as const satisfies Record<keyof typeof switches, ReactNode>

        return rendererComponentMap[keyField]
    }

    const handleResetRelatedFields = (keyField: keyof typeof switches) => {
        switch (keyField) {
            case 'civilDisability': {
                form.setValue(rendererKeyMap[keyField], 0)
                break
            }
            default: {
                form.setValue(rendererKeyMap[keyField], false)
            }
        }
    }

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            {ObjectKeys(switches).map(switchKey => (
                <SwitchField
                    key={switchKey}
                    fullWidth
                    columns={3}
                    height={switchKey == 'civilDisability' ? '58px' : 'auto'}
                >
                    <Controller
                        name={switchKey}
                        render={({field}) => (
                            <>
                                <GridItem spanColumn={1}>
                                    <Switch
                                        size="sm"
                                        label={t(switches[switchKey])}
                                        defaultChecked={field.value}
                                        onChange={value => {
                                            field.onChange(value)
                                            handleResetRelatedFields(switchKey)
                                        }}
                                    />
                                </GridItem>
                                <GridItem spanColumn={2}>{renderSwitchRelatedField(switchKey)}</GridItem>
                            </>
                        )}
                    />
                </SwitchField>
            ))}
            {ObjectKeys(lawCheckboxes).map(checkboxKey => (
                <Checkbox
                    key={checkboxKey}
                    id={checkboxKey}
                    label={t(lawCheckboxes[checkboxKey])}
                    containerClass="reopening-checkbox-container"
                    {...form.register(checkboxKey)}
                />
            ))}
        </Flexbox>
    )
}
